import React, { useState } from "react";
import { Link } from "react-router-dom";
import { IOrganizationProps } from "../../types/Organization";
import { ISmallEventProps } from "../../types";
import { getEventUrl, isCustomDomain } from "../../lib/utils/domain";
import TheDRANavbar from "../../components/TheDRANavbar";
import { TheDRALogo } from "../../components/TheDRANavbar/TheDRALogo";
import { Login } from "../../components/Login";
import { AnimatePresence } from "../../components/AnimatePresenceWrapper";
import { PerformRequest } from "../../lib/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import { HttpCodes } from "../../lib/data";
import { toast } from "sonner";
import "./styles.scss";

interface OrganizationHomepageTheDRAProps {
  organization: IOrganizationProps;
  events: ISmallEventProps[];
}

const OrganizationHomepageTheDRA: React.FC<OrganizationHomepageTheDRAProps> = ({
  organization,
  events: initialEvents,
}) => {
  const [email, setEmail] = useState("");
  const [showThanks, setShowThanks] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);

  // Format time from start_time and end_time
  const formatEventTime = (startTime?: string, endTime?: string): string => {
    if (!startTime) return "";
    const formatTime = (timeString: string): string => {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    };
    const formattedStart = formatTime(startTime);
    const formattedEnd = endTime ? formatTime(endTime) : null;
    return formattedEnd
      ? `${formattedStart} - ${formattedEnd}`
      : formattedStart;
  };

  // Get location string based on event type
  const getEventLocation = (eventType?: string): string => {
    switch (eventType) {
      case "online":
        return "Online Event";
      case "hybrid":
        return "Hybrid Event";
      case "offline":
        return "Venue TBA";
      default:
        return "Location TBA";
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !organization?.id) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setIsSubscribing(true);
    try {
      const response = await PerformRequest({
        method: Endpoints.GetSubscribeToNewsletter().method,
        route: Endpoints.GetSubscribeToNewsletter().route,
        data: {
          email: email,
          organization_id: organization.id,
        },
        doNotUseAuthorization: true,
      });

      if (
        response.status === HttpCodes.Success ||
        response.status === HttpCodes.Created
      ) {
        setShowThanks(true);
        setEmail("");
        toast.success("Successfully subscribed to newsletter!");
        setTimeout(() => setShowThanks(false), 3000);
      } else {
        toast.error(
          response.message || "Failed to subscribe. Please try again."
        );
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to subscribe. Please try again.");
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="thedra-homepage">
      {/* Header */}
      <TheDRANavbar
        onLoginClick={() => setShowLogin(true)}
        organizationSlug={organization?.slug || "thedra"}
      />

      {/* Hero Section - Hidden */}
      {/* <section className="thedra-hero" id="home">
        <div
          className="thedra-hero-background"
          style={{
            backgroundImage: `url(${thedraHeroImage})`,
          }}
        ></div>
        <div className="thedra-hero-logo">
          <TheDRALogo />
        </div>
        <div className="thedra-hero-text-block">
          <p className="thedra-hero-description">
            Welcome to The Digital Risk Alliance (TheDRA). We host and manage
            events that bring together leaders, practitioners, and experts to
            address digital risk challenges and advance digital trust.
          </p>
          <button
            className="thedra-hero-discover-btn"
            onClick={handleDiscoverMore}
          >
            Discover More
          </button>
        </div>
      </section> */}

      {/* About Section */}
      <section id="about-section" className="thedra-about">
        <div className="thedra-container">
          <h2 className="thedra-section-title">About TheDRA.</h2>
          <p className="thedra-section-subtitle">
            The Digital Risk Alliance (TheDRA) is a global member-based
            nonprofit organization dedicated to hosting and managing events that
            address digital risk challenges.
          </p>
        </div>
      </section>

      {/* Events Section */}
      <section id="events" className="thedra-events">
        <div className="thedra-container">
          <div className="thedra-events-header">
            <h2 className="thedra-section-title">Events</h2>
            <Link
              to={
                isCustomDomain()
                  ? "/events"
                  : `/organization/${organization.slug}/events`
              }
              className="thedra-view-all-events-btn"
            >
              View all Events
            </Link>
          </div>
          {initialEvents && initialEvents.length > 0 ? (
            <div className="thedra-events-grid">
              {initialEvents.slice(0, 6).map((event) => {
                const eventDate = event.date
                  ? new Date(event.date).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "Date TBA";
                const eventTime = formatEventTime(
                  event.start_time,
                  event.end_time
                );
                const eventLocation = getEventLocation(event.type);
                const eventUrl = getEventUrl(
                  event.id.toString(),
                  organization.slug
                );

                return (
                  <div key={event.id} className="thedra-event-card">
                    {event.cover_image && (
                      <div className="thedra-event-image">
                        <Link to={eventUrl}>
                          <img src={event.cover_image} alt={event.name} />
                        </Link>
                      </div>
                    )}
                    <div className="thedra-event-content">
                      <h3 className="thedra-event-title">
                        <Link to={eventUrl}>{event.name}</Link>
                      </h3>
                      <p className="thedra-event-date">{eventDate}</p>
                      {eventTime && (
                        <p className="thedra-event-time">{eventTime}</p>
                      )}
                      {eventLocation && (
                        <p className="thedra-event-location">{eventLocation}</p>
                      )}
                      {event.short_description && (
                        <p className="thedra-event-description">
                          {event.short_description}
                        </p>
                      )}
                      <Link to={eventUrl} className="thedra-event-button">
                        <button>Learn More</button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="thedra-no-events">No events at the moment</p>
          )}
        </div>
      </section>

      {/* Email Signup Section */}
      <section className="thedra-signup">
        <div className="thedra-container">
          <h2 className="thedra-signup-title">Join the Alliance Today!</h2>
          <p className="thedra-signup-subtitle">
            Join our email list and get access to specials deals exclusive to
            our subscribers.
          </p>
          <form className="thedra-signup-form" onSubmit={handleEmailSubmit}>
            <input
              type="email"
              placeholder="Enter your email here"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubscribing}
            />
            <button type="submit" disabled={isSubscribing}>
              {isSubscribing ? "Subscribing..." : "Sign Up"}
            </button>
          </form>
          {showThanks && (
            <p className="thedra-signup-thanks">Thanks for submitting!</p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="thedra-footer">
        <div className="thedra-container">
          <div className="thedra-footer-content">
            <div className="thedra-footer-left">
              <div className="thedra-footer-logo">
                <TheDRALogo />
              </div>
              <p className="thedra-footer-email">
                {organization.email || "info@TheDRA.org"}
              </p>
              <a
                href={organization.linkedin_link || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="thedra-footer-linkedin"
              >
                in
              </a>
            </div>
            <div className="thedra-footer-right">
              <div className="thedra-footer-links">
                <a href="#privacy" className="thedra-footer-link">
                  Privacy Policy
                </a>
                <a href="#accessibility" className="thedra-footer-link">
                  Accessibility Statement
                </a>
              </div>
              <p className="thedra-footer-copyright">
                © {new Date().getFullYear()} by{" "}
                {organization.name || "The Digital Risk Alliance (TheDRA)"}.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      <AnimatePresence>
        {showLogin && (
          <Login
            onClose={() => setShowLogin(false)}
            organizationName={organization?.name || "TheDRA"}
            organizationId={organization?.id || 0}
            organizationSlug={organization?.slug || "thedra"}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default OrganizationHomepageTheDRA;
